import React from "react";
import {
    Box,
    Typography,
    FormControlLabel,
    Checkbox,
    IconButton,
    Stack,
} from "@mui/material";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxButton from "components/UI/AcxButton";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { CustomFieldType } from "./types";
import theme from "Theme/AppTheme";

interface ManageExtendedMetadataDialogProps {
    isOpen: boolean;
    customTargetFields: CustomFieldType[];
    manageExtendedData: Array<
        CustomFieldType & { isEditing: boolean; existingNameError?: string }
    >;
    removeExtendedData: (id: string) => void;
    onClose: () => void;
    updateExtendedData: (
        id: string,
        updatedData: {
            label?: string;
            value?: string | number;
            isEditing?: boolean;
            isIndexed?: boolean;
            existingNameError?: string;
        },
    ) => void;
    setCustomTargetFields: (fields: Array<any>) => void;
}

const ManageExtendedMetadataDialog: React.FC<ManageExtendedMetadataDialogProps> =
    ({
        isOpen,
        customTargetFields,
        manageExtendedData,
        removeExtendedData,
        onClose,
        updateExtendedData,
        setCustomTargetFields,
    }) => {
        const [searchValue, setSearchValue] = React.useState("");

        const saveFieldName = (id: string, label: string) => {
            // check if name already exists
            const existingName = manageExtendedData.some(
                (data) => data.label === label && data.id !== id,
            );
            if (existingName) {
                updateExtendedData(id, {
                    existingNameError: label,
                });
            } else {
                updateExtendedData(id, {
                    isEditing: false,
                    existingNameError: "",
                });
            }
        };

        const handleClose = () => {
            setSearchValue("");
            onClose();
        };

        // Filter extended data by search value
        const filteredExtendedData = searchValue.trim()
            ? manageExtendedData.filter((data) =>
                  data.label?.toLowerCase().includes(searchValue.toLowerCase()),
              )
            : manageExtendedData;

        return (
            <AcxDialog
                title="Manage Extended Metadata"
                isOpen={isOpen}
                onClose={handleClose}
                contentWidth="540px"
                dialogContentChildren={
                    <>
                        <Box sx={{ mb: 2 }}>
                            <AcxMainTextField
                                id="extended-metadata-search"
                                fullWidth
                                placeholderText="Search extended metadata..."
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                containerStyle={{ margin: theme.spacing(0, 1) }}
                                endAdornment={
                                    <IconButton
                                        onClick={() => setSearchValue("")}
                                        sx={{ p: 0, mr: 1 }}
                                    >
                                        <CloseIcon
                                            sx={{ width: 20, height: 20 }}
                                        />
                                    </IconButton>
                                }
                            />
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                height: "32vh",
                                overflowY: "auto",
                            }}
                        >
                            {filteredExtendedData.map((data) => {
                                const {
                                    id,
                                    label,
                                    isEditing,
                                    isIndexed,
                                    existingNameError,
                                } = data;

                                return (
                                    <Stack
                                        key={id}
                                        sx={{
                                            minHeight: 58,
                                            background: theme.palette.gray[50],
                                            borderRadius: 2,
                                            border: `1px solid ${theme.palette.gray[200]}`,
                                            boxShadow:
                                                "0px 1px 2px 0px rgba(0, 0, 0, 0.07);",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                flex: 1,
                                            }}
                                        >
                                            {isEditing ? (
                                                <AcxMainTextField
                                                    id={`custom-field-name-${id}`}
                                                    fullWidth
                                                    placeholderText="Enter..."
                                                    containerStyle={{
                                                        flex: 1,
                                                        margin: theme.spacing(
                                                            0,
                                                            1,
                                                        ),
                                                    }}
                                                    sx={{ fontSize: 16 }}
                                                    value={label}
                                                    endAdornment={
                                                        <IconButton
                                                            onClick={() =>
                                                                saveFieldName(
                                                                    id,
                                                                    label,
                                                                )
                                                            }
                                                            size="small"
                                                        >
                                                            <CheckIcon />
                                                        </IconButton>
                                                    }
                                                    onChange={(e) =>
                                                        updateExtendedData(id, {
                                                            value: e.target
                                                                .value,
                                                            label: e.target
                                                                .value,
                                                        })
                                                    }
                                                />
                                            ) : (
                                                <Typography
                                                    sx={{
                                                        flex: 1,
                                                        fontSize: 16,
                                                        padding: 2,
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                            )}
                                            {isEditing && (
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={isIndexed}
                                                            onChange={(e) =>
                                                                updateExtendedData(
                                                                    id,
                                                                    {
                                                                        isIndexed:
                                                                            e
                                                                                .target
                                                                                .checked,
                                                                    },
                                                                )
                                                            }
                                                            sx={{
                                                                padding: 1,
                                                            }}
                                                        />
                                                    }
                                                    label="Reportable"
                                                    sx={{
                                                        paddingRight: 2,
                                                        borderRadius: 1,
                                                        margin: 0,
                                                    }}
                                                />
                                            )}
                                            {isEditing ? (
                                                <IconButton
                                                    onClick={() => {
                                                        updateExtendedData(id, {
                                                            isEditing: false,
                                                            existingNameError:
                                                                "",
                                                            label: customTargetFields.find(
                                                                (f) =>
                                                                    f.id === id,
                                                            )?.label,
                                                            value: customTargetFields.find(
                                                                (f) =>
                                                                    f.id === id,
                                                            )?.value,
                                                        });
                                                    }}
                                                >
                                                    <CloseIcon />
                                                </IconButton>
                                            ) : (
                                                <IconButton
                                                    onClick={() =>
                                                        updateExtendedData(id, {
                                                            isEditing: true,
                                                        })
                                                    }
                                                    sx={{
                                                        color: theme.palette
                                                            .primary.main,
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                onClick={() =>
                                                    removeExtendedData(id)
                                                }
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                        {existingNameError && (
                                            <Typography
                                                sx={{
                                                    padding: 0.5,
                                                    color: theme.palette.error
                                                        .main,
                                                    mx: 1,
                                                }}
                                            >
                                                An extended metadata field with
                                                the name{" "}
                                                <strong>
                                                    "{existingNameError}"
                                                </strong>{" "}
                                                already exists.
                                            </Typography>
                                        )}
                                    </Stack>
                                );
                            })}
                        </Box>
                    </>
                }
            >
                <AcxButton
                    color="primary"
                    onClick={() => {
                        setCustomTargetFields(
                            manageExtendedData.map((field) => ({
                                ...field,
                            })),
                        );
                        handleClose();
                    }}
                >
                    Save Changes
                </AcxButton>
            </AcxDialog>
        );
    };

export default ManageExtendedMetadataDialog;
